import { t } from 'i18next';
import { useMutation } from 'react-query';

import { bomComponentService } from 'bom/services';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';

interface IBomComponentActions {
  notificationPosition?: NotificationPosition;
  selectionCount?: number;
}

export const useBomComponentActions = ({
  notificationPosition = NotificationPosition.TOP,
  selectionCount,
}: IBomComponentActions = {}) => ({
  ...useUpsertBomComponent(notificationPosition),
  ...useUpsertBomComponents(),
  ...useRemoveBomComponents(selectionCount),
  ...useRestoreBomComponents(selectionCount),
});

const useUpsertBomComponent = (notificationPosition = NotificationPosition.TOP) => {
  const { errors } = useHandleErrors({
    success: ({ component }) =>
      component.id
        ? { message: t('messageComponentUpdateSuccess') }
        : { message: t('messageComponentCreateSuccess') },
    loading: ({ component }) =>
      component.id
        ? { message: t('messageComponentUpdateInProgress') }
        : { message: t('messageComponentCreateInProgress') },
    error: ({ component }) =>
      component.id
        ? { title: t('errorTitleComponentUpdate') }
        : { title: t('errorTitleComponentCreate') },
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(bomComponentService.upsertBomComponent, {
    ...errors,
  });

  return { upsertBomComponent: mutateAsync };
};

const useUpsertBomComponents = () => {
  const { errors } = useHandleErrors({
    success: ({ components }) =>
      components[0].id
        ? { message: t('messageComponentsUpdateSuccess', { count: components.length }) }
        : { message: t('messageComponentsCreateSuccess', { count: components.length }) },
    loading: ({ components }) =>
      components[0].id
        ? { message: t('messageComponentsUpdateInProgress', { count: components.length }) }
        : { message: t('messageComponentsCreateInProgress', { count: components.length }) },
    error: ({ components }) =>
      components[0].id
        ? { title: t('errorTitleComponentsUpdate', { count: components.length }) }
        : { title: t('errorTitleComponentsCreate', { count: components.length }) },
    invalidateQueries: ['jobParts', 'elements'],
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(bomComponentService.upsertBomComponents, {
    ...errors,
  });

  return { upsertBomComponents: mutateAsync };
};

const useRemoveBomComponents = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageJobPartLinesRemoveSuccess', { count: selectionCount }) }),
    loading: () => ({
      message: t('messageJobPartLinesRemoveInProgress', { count: selectionCount }),
    }),
    error: () => ({ title: t('errorTitleJobPartLinesRemove', { count: selectionCount }) }),
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(bomComponentService.removeBomComponents, {
    ...errors,
  });

  return { removeBomComponents: mutateAsync };
};

const useRestoreBomComponents = (selectionCount?: number) => {
  const { errors } = useHandleErrors({
    success: () => ({ message: t('messageJobPartLinesRestoreSuccess', { count: selectionCount }) }),
    loading: () => ({
      message: t('messageJobPartLinesRestoreInProgress', { count: selectionCount }),
    }),
    error: () => ({ title: t('errorTitleJobPartLinesRestore', { count: selectionCount }) }),
    position: NotificationPosition.TOP,
  });

  const { mutateAsync } = useMutation(bomComponentService.restoreBomComponents, {
    ...errors,
  });

  return { restoreBomComponents: mutateAsync };
};
